import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantJobListStatusRow from "../components/ApplicantJobListStatusRow";
import ApplicantJobSearchResultCard from "../components/ApplicantJobSearchResultCard";
import SiteBreadcrumbs from "../components/SiteBreadcrumbs";
import { getApplicantSavedJobPosts, isApiConfigured } from "../apiClient";
import { jobPublicHref } from "../lib/jobPublicPath";

const PAGE_SIZE = 10;

function ApplicantSavedJobsPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const sentinelRef = useRef(null);
  const wasIntersectingRef = useRef(false);
  const listLengthRef = useRef(0);

  const loadPage = useCallback(
    async (offset) => {
      const t = String(authToken || "").trim();
      if (!t || !isApiConfigured()) {
        throw new Error("Not signed in.");
      }
      return getApplicantSavedJobPosts(t, { limit: PAGE_SIZE, offset });
    },
    [authToken]
  );

  useEffect(() => {
    listLengthRef.current = jobs.length;
  }, [jobs.length]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== "applicant" || !isApiConfigured() || !String(authToken || "").trim()) {
      setJobs([]);
      setTotal(0);
      setHasMore(false);
      setLoading(false);
      setError("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { jobs: rows, total: n, hasMore: more } = await loadPage(0);
        if (!cancelled) {
          setJobs(rows);
          setTotal(n);
          setHasMore(more);
        }
      } catch (e) {
        if (!cancelled) {
          setJobs([]);
          setTotal(0);
          setHasMore(false);
          setError(e instanceof Error ? e.message : "Could not load saved jobs.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, authToken, loadPage]);

  const fetchMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const offset = listLengthRef.current;
      const { jobs: next, total: n, hasMore: more } = await loadPage(offset);
      setJobs((prev) => {
        const seen = new Set(prev.map((j) => j.id));
        const merged = [...prev];
        for (const j of next) {
          if (!seen.has(j.id)) {
            seen.add(j.id);
            merged.push(j);
          }
        }
        return merged;
      });
      setTotal(n);
      setHasMore(more);
    } catch {
      /* keep existing rows */
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loading, loadPage]);

  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const now = entry.isIntersecting;
        const crossedInto = now && !wasIntersectingRef.current;
        wasIntersectingRef.current = now;
        if (crossedInto && hasMore && !loadingMore) void fetchMore();
      },
      { root: null, rootMargin: "0px 0px 140px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading, fetchMore]);

  const openJob = (job) => {
    navigate(jobPublicHref(job), {
      state: {
        fromPath: "/applicant/saved-jobs",
        fromLabel: "Saved jobs",
      },
    });
  };

  const countLabel = total === 1 ? "1 saved job" : `${total} saved jobs`;

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <main className="nd1-shell nd1-applicant-main nd1-applicant-job-search-page applicant-home job-search-page">
        <header className="nd1-applicant-job-search-page__intro">
          <SiteBreadcrumbs
            className="nd1-applicant-breadcrumbs"
            items={[
              { name: "Applicant home", path: "/applicant-home" },
              { name: "Saved jobs", path: "/applicant/saved-jobs", current: true },
            ]}
          />
          <h1 className="nd1-applicant-job-search-page__title">Saved jobs</h1>
          <p className="nd1-applicant-meta">
            {loading
              ? "Loading your saved roles…"
              : total > 0
                ? "Roles you saved for later — newest first."
                : "Save jobs from search and they'll show up here."}
          </p>
        </header>

        <section className="nd1-applicant-section" aria-labelledby="saved-jobs-results-title">
          <div className="nd1-applicant-section-card">
            {error ? (
              <p className="nd1-applicant-meta" role="alert">
                {error}
              </p>
            ) : null}

            {loading ? (
              <p className="nd1-applicant-meta" aria-live="polite">
                Loading…
              </p>
            ) : jobs.length === 0 && !error ? (
              <div className="nd1-applicant-empty-state">
                <div
                  className="nd1-applicant-empty-state__icon-wrap nd1-applicant-empty-state__icon-wrap--orange"
                  aria-hidden="true"
                >
                  <Heart className="nd1-applicant-empty-state__icon" size={36} strokeWidth={1.75} fill="currentColor" />
                </div>
                <div className="nd1-applicant-empty-state__copy">
                  <p className="nd1-applicant-empty-state__title">No saved jobs yet</p>
                  <p className="nd1-applicant-empty-state__text">
                    Tap the heart on a job in search to save it for later.
                  </p>
                  <Link to="/job-search" className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm">
                    Browse jobs
                  </Link>
                </div>
              </div>
            ) : (
              <section className="nd1-applicant-job-search-results-section job-search-results">
                <div className="nd1-applicant-section-head nd1-applicant-job-search-results-head">
                  <div className="nd1-applicant-section-head__title-wrap">
                    <span
                      className="nd1-applicant-section-head__icon-wrap nd1-applicant-section-head__icon-wrap--orange"
                      aria-hidden="true"
                    >
                      <Heart size={20} strokeWidth={1.75} fill="currentColor" />
                    </span>
                    <h2 id="saved-jobs-results-title" className="nd1-applicant-section-head__title">
                      {countLabel}
                    </h2>
                  </div>
                </div>

                <div className="nd1-applicant-job-search-results">
                  {jobs.map((job, index) => (
                    <ApplicantJobSearchResultCard
                      key={job.id}
                      job={job}
                      index={index}
                      onOpen={() => openJob(job)}
                      statusRow={
                        <ApplicantJobListStatusRow statusKey="saved" timestampIso={job.savedAt} />
                      }
                    />
                  ))}
                </div>

                {hasMore ? <div ref={sentinelRef} className="job-search-lazy-sentinel" aria-hidden="true" /> : null}
                {loadingMore ? (
                  <p className="nd1-applicant-meta" aria-live="polite">
                    Loading more…
                  </p>
                ) : null}
                {!hasMore && jobs.length > 0 ? (
                  <div className="job-search-end-of-list">
                    <p className="nd1-applicant-meta job-search-end-eyebrow">You are at the end</p>
                  </div>
                ) : null}
              </section>
            )}
          </div>
        </section>
      </main>
    </ApplicantShell>
  );
}

export default ApplicantSavedJobsPage;
